import { INSTALLER_TAG_PATTERN } from '../../deployment/run-installer.js';
import { RUN_COMMANDS } from '../../runs/run.js';
/**
 * The Azure dispatcher: a Consumption Logic App whose whole definition is in
 * the runner template (IM-43 Q4).
 *
 * The AWS dispatcher is a Lambda with its code inlined, so a customer reads
 * exactly what runs. ARM cannot inline Functions code, but a Logic App's
 * `definition` *is* inline, so the property carries over: nothing here can be
 * swapped after review.
 *
 * What it does, in order:
 *
 * 1. **Admits one caller.** The trigger's Entra policy names Qrvey's tenant
 *    and the dispatch app's `appid`, and SAS is disabled, so a leaked trigger
 *    URL is not a way in. That is the AWS resource policy naming one role.
 * 2. **Validates the request against `DISPATCH_REQUEST_SCHEMA`.** The schema
 *    is the allow list: a closed set of fields, the run commands, and the tag
 *    grammar. The Workflow Definition Language has no regex, so `pattern` is
 *    the only place a tag can be checked. Whether Logic Apps honours `pattern`
 *    is on the verify-live list; until it is shown to, the runner wrapper
 *    must check the tag again.
 * 3. **Checks the grant, as a courtesy.** It reads the deploy secret's
 *    metadata (never its value) and refuses a run whose secret has expired,
 *    so the caller gets `grant-expired` instead of a container that fails to
 *    sign in. Entra is what actually closes access, at the secret's
 *    `endDateTime`; this cannot widen it.
 * 4. **Starts the job as its own identity**, pinning the image to the job's
 *    own repository. It reads the job first and starts it with that
 *    container plus this run's settings, so a run can change the tag and
 *    nothing else about what runs.
 *
 * Only this identity may start the job. The control plane never holds
 * `jobs/start`, because a start can override the image (measured in IM-43),
 * and whoever can start the job can run any image as the runner.
 */
/** The trigger's name, which is part of its URL. */
export const DISPATCH_TRIGGER = 'dispatch';
/**
 * The tag grammar, with the anchors moved so an empty tag also matches: the
 * control plane sends `''` for "the job's own image", as it does on AWS.
 */
const TAG_OR_EMPTY = `^(${INSTALLER_TAG_PATTERN.replace(/^\^/, '').replace(/\$$/, '')})?$`;
/**
 * What a run request may carry, and nothing else.
 *
 * `requestedBy` has no AWS counterpart. The customer's Activity Log names
 * Qrvey's app, never the engineer (IM-43 Q3), so the engineer is carried here
 * and the dispatcher's own run history, which the customer holds, records it.
 *
 * Nothing in it is a secret, and nothing may become one: the job execution's
 * environment is readable through Reader, which is how completion is polled.
 */
export const DISPATCH_REQUEST_SCHEMA = {
    type: 'object',
    additionalProperties: false,
    required: ['command', 'runId', 'requestedBy'],
    properties: {
        command: { type: 'string', enum: [...RUN_COMMANDS] },
        runId: { type: 'string', pattern: '^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$' },
        requestedBy: { type: 'string', minLength: 3, maxLength: 254, pattern: '^[^@\\s]+@[^@\\s]+$' },
        variables: { type: 'object' },
        stateKey: { type: 'string', pattern: '^[A-Za-z0-9][\\w./-]{0,255}$' },
        imageTag: { type: 'string', pattern: TAG_OR_EMPTY },
    },
};
const ARM = 'https://management.azure.com';
const MSI = { type: 'ManagedServiceIdentity', audience: `${ARM}/` };
/** The job's own container, as the dispatcher read it. */
const OWN = "first(body('Read_job')?['properties']?['template']?['containers'])";
const TAG = "coalesce(triggerBody()?['imageTag'], '')";
/** The deploy secret's expiry, in Unix seconds, or null when it has none. */
const EXP = "body('Read_grant')?['properties']?['attributes']?['exp']";
const NOW_SECONDS = 'div(sub(ticks(utcNow()), 621355968000000000), 10000000)';
const respond = (status, body, runAfter) => ({
    type: 'Response',
    kind: 'Http',
    inputs: { statusCode: status, body },
    runAfter,
});
/** The environment this run adds to the job's own. No value here is a secret. */
const RUN_ENV = [
    { name: 'QIM_RUN_ID', value: "@{triggerBody()?['runId']}" },
    { name: 'QIM_COMMAND', value: "@{triggerBody()?['command']}" },
    { name: 'QIM_REQUESTED_BY', value: "@{triggerBody()?['requestedBy']}" },
    { name: 'QIM_STATE_KEY', value: "@{coalesce(triggerBody()?['stateKey'], '')}" },
    { name: 'QIM_VARIABLES', value: "@{string(coalesce(triggerBody()?['variables'], json('{}')))}" },
];
/** The installer's argv for each command, as the AWS dispatcher's `CMD`. */
const ARGS = "if(equals(triggerBody()?['command'], 'destroy'), createArray('destroy', '--approve'), createArray(triggerBody()?['command']))";
/** The job's image, or the same repository at the tag this run names. */
const IMAGE = `if(equals(${TAG}, ''), ${OWN}?['image'], concat(parameters('runnerRepository'), ':', ${TAG}))`;
/**
 * The workflow definition: the code the customer reviews.
 *
 * Its parameters are filled by the runner template, so the definition itself
 * is the same document in every subscription.
 */
export const DISPATCHER_DEFINITION = {
    $schema: 'https://schema.management.azure.com/providers/Microsoft.Logic/schemas/2016-06-01/workflowdefinition.json#',
    contentVersion: '1.0.0.0',
    parameters: {
        jobId: { type: 'String' },
        vaultId: { type: 'String' },
        deploySecretName: { type: 'String' },
        runnerRepository: { type: 'String' },
        runImageTag: { type: 'String' },
    },
    triggers: {
        [DISPATCH_TRIGGER]: {
            type: 'Request',
            kind: 'Http',
            // Refuse a body the schema does not describe, with a 400, before any
            // action runs. Without this the schema only names tokens.
            operationOptions: 'EnableSchemaValidation',
            inputs: { method: 'POST', schema: DISPATCH_REQUEST_SCHEMA },
        },
    },
    actions: {
        /*
         * The image, before anything reaches the account's job: another tag is
         * refused where the customer has said so (`runImageTag = fixed`), as
         * IM-197's dispatcher does.
         */
        Check_tag: {
            type: 'If',
            runAfter: {},
            expression: {
                and: [
                    { equals: ["@parameters('runImageTag')", 'fixed'] },
                    { not: { equals: [`@${TAG}`, ''] } },
                ],
            },
            actions: {
                Refuse_tag: respond(409, { error: 'image-tag-fixed' }, {}),
                Stop_tag: {
                    type: 'Terminate',
                    runAfter: { Refuse_tag: ['Succeeded'] },
                    inputs: { runStatus: 'Cancelled' },
                },
            },
            else: { actions: {} },
        },
        // The deploy secret's metadata through ARM: its expiry and nothing else.
        // A management-plane read cannot return a secret's value.
        Read_grant: {
            type: 'Http',
            runAfter: { Check_tag: ['Succeeded'] },
            inputs: {
                method: 'GET',
                uri: `${ARM}@{parameters('vaultId')}/secrets/@{parameters('deploySecretName')}?api-version=2023-07-01`,
                authentication: MSI,
            },
        },
        No_grant: respond(409, {
            error: 'grant-missing',
            detail: 'The deploy-access script has not stored a secret in this vault.',
        }, { Read_grant: ['Failed', 'TimedOut'] }),
        Check_grant: {
            type: 'If',
            runAfter: { Read_grant: ['Succeeded'] },
            // An expiry in the past refuses. No expiry at all is left to Entra,
            // which is what decides either way.
            expression: {
                and: [
                    { not: { equals: [`@${EXP}`, null] } },
                    { less: [`@int(${EXP})`, `@${NOW_SECONDS}`] },
                ],
            },
            actions: {
                Grant_expired: respond(403, { error: 'grant-expired', expiredAt: `@${EXP}` }, {}),
            },
            else: {
                actions: {
                    Read_job: {
                        type: 'Http',
                        runAfter: {},
                        inputs: {
                            method: 'GET',
                            uri: `${ARM}@{parameters('jobId')}?api-version=2024-03-01`,
                            authentication: MSI,
                        },
                    },
                    // A Compose, because `@{…}` is interpolated in an action's inputs and
                    // never inside an expression's string literal.
                    Run_env: { type: 'Compose', runAfter: { Read_job: ['Succeeded'] }, inputs: RUN_ENV },
                    Start_job: {
                        type: 'Http',
                        runAfter: { Run_env: ['Succeeded'] },
                        inputs: {
                            method: 'POST',
                            uri: `${ARM}@{parameters('jobId')}/start?api-version=2024-03-01`,
                            authentication: MSI,
                            body: {
                                containers: [
                                    `@setProperty(setProperty(setProperty(${OWN}, 'image', ${IMAGE}), 'args', ${ARGS}), 'env', union(coalesce(${OWN}?['env'], createArray()), outputs('Run_env')))`,
                                ],
                            },
                        },
                    },
                    Started: respond(202, { runId: "@{triggerBody()?['runId']}", execution: "@{body('Start_job')?['name']}" }, { Start_job: ['Succeeded'] }),
                    Start_failed: respond(502, { error: 'start-failed', status: "@{outputs('Start_job')?['statusCode']}" }, { Start_job: ['Failed', 'TimedOut'] }),
                    Job_unreadable: respond(502, { error: 'job-unreadable', status: "@{outputs('Read_job')?['statusCode']}" }, { Read_job: ['Failed', 'TimedOut'] }),
                },
            },
        },
    },
    outputs: {},
};
//# sourceMappingURL=dispatcher.js.map